import { Container, Divider } from "@material-ui/core";
import { Add, Cached, Remove } from "@material-ui/icons";
import { Box } from "@mui/material";
import axios from "axios";
import {
  CurrencyDisplay,
  ModalError,
  Page,
  SectionLabel,
  SkeletonComponent,
  TableAction,
} from "components";
import { InnoTableV2 } from "inno-ui";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors } from "utils";
import DetailPaymentMutation from "./DetailPaymentMutation";

const PaymentMutation = () => {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    status: "",
    search: "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [dataTableDetail, setDataTableDetail] = useState([]);
  const [showDetail, setShowDetail] = useState(false);

  const [queryParamsDetail, setQueryParamsDetail] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPageDetail, setTableTotalPageDetail] = useState(1);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handleChangeQueryParamsDetail = (value, key) => {
    setQueryParamsDetail(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handleShowDetail = id => {
    getDetailDataTable(id);
    setShowDetail({ id: id, isShow: true });
  };

  const handleCloseDetail = () => {
    setShowDetail({ isShow: false });
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/composer/transaction/v2/balance/mutation`,
        { headers, params: queryParams }
      );
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  const getDetailDataTable = async id => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/composer/transaction/v2/balance/mutation/${id}`,
        { headers, params: queryParamsDetail }
      );
      const { data, meta } = res?.data;
      setDataTableDetail(data || []);
      const pageCount = getTotalPage(meta?.total, queryParamsDetail?.size);
      setTableTotalPageDetail(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  useEffect(() => {
    getDataTable();
  }, [queryParams]);

  useEffect(() => {
    if (showDetail.id) getDetailDataTable(showDetail.id);
  }, [queryParamsDetail]);

  return (
    <Page className={classes.root} title="Balance Mutation">
      <Container maxWidth={false}>
        <HeaderTitle
          title="Balance Mutation"
          breadcrumbData={breadcrumbData}
          backButton
        />
        <Divider className={classes.divider} />
        <SectionLabel
          title="Balance Mutation"
          subTitle="Feature for showing balance mutation in payment transaction"
          mb="16px"
        />
        {loadingPage ? (
          <SkeletonComponent variant="wave" />
        ) : (
          <InnoTableV2
            isLoading={false}
            columns={columnTable}
            items={dataTable}
            page={queryParams?.page}
            rowsPerPage={queryParams?.size}
            totalPage={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangeRowsPerPage={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
            isHaveAction
            renderAction={item => (
              <TableAction
                handleView={() => handleShowDetail(item?.id)}
                tooltipView="View"
              />
            )}
          />
        )}
      </Container>
      <DetailPaymentMutation
        modalVisible={showDetail.isShow}
        onClose={handleCloseDetail}
        detailReport={dataTableDetail}
        queryParamsDetail={queryParamsDetail}
        tableTotalPageDetail={tableTotalPageDetail}
        handleChangeQueryParams={handleChangeQueryParamsDetail}
      />
    </Page>
  );
};
const breadcrumbData = [
  {
    label: "Home",
    link: "/pencipta/dashboard-pencipta",
  },
  {
    label: "Transaction",
    link: "/pencipta/payment",
  },
  {
    label: "Payment",
    link: "/pencipta/payment",
  },
  {
    label: "Balance Mutation",
    active: true,
  },
];
const columnTable = [
  {
    name: "date",
    title: "Due Date",
    renderText: item => item && formatDate(item),
  },
  {
    name: "transaction_name",
    title: "Type Transaction",
    renderText: item => item || "-",
  },
  {
    name: "transaction_number",
    title: "Document No",
    renderText: item => item || "-",
  },
  {
    name: "description",
    title: "Description",
    renderText: item => item || "-",
  },
  {
    name: "all",
    title: "Value",
    headerAlign: "right",
    renderText: item => {
      const colorReserve = {
        color: "#295ECC",
      };
      const colorIn = {
        color: "rgb(22 207 36)",
      };
      const colorOut = {
        color: "rgb(237 23 53)",
      };
      const unReserve = {
        color: "rgb(237 161 23)",
      };
      return (
        <Box
          color={
            item.balance_tag === "reserved"
              ? unReserve
              : item.balance_tag === "in"
              ? colorIn
              : item.balance_tag === "out"
              ? colorOut
              : item.balance_tag === "paid"
              ? colorOut
              : colorReserve
          }
          display={"flex"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          gap={"2px"}
          fontSize="14px"
        >
          {item.balance_tag === "in" ? (
            <Add />
          ) : item.balance_tag === "advance" ||
            item.balance_tag === "out" ||
            item.balance_tag === "paid" ? (
            <Remove />
          ) : (
            <Cached />
          )}
          <CurrencyDisplay value={item?.value} prefix="Rp" />
        </Box>
      );
    },
  },
  {
    name: "balance",
    title: "Balance",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} prefix="Rp" />,
  },
];

export default PaymentMutation;
